import { Component, ElementRef, OnInit, ViewChild, Input } from '@angular/core';
import { MerchantService, TiktokPushProductConfig } from "../../services/merchant/merchant.service";
import { Global } from "../../classes/global";
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from "ngx-toastr";
import { FormArray, FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-tiktok-push-product-config',
  templateUrl: './tiktok-push-product-config.component.html',
  styleUrls: ['./tiktok-push-product-config.component.scss']
})
export class TiktokPushProductConfigComponent implements OnInit {

  static readonly EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

  @ViewChild('tiktokPushProductConfigModal') tiktokPushProductConfigModal;
  @ViewChild('importTemplateModal') importTemplateModal;
  @Input() merchant: any;

  configForm: FormGroup;
  isEditMode = false;
  configId: string;

  // Dropdown Data
  tiktokConfigs: any[] = [{ configName: 'Add New', id: TiktokPushProductConfigComponent.EMPTY_GUID }];
  selectedConfigId: any;

  // Category search properties
  categorySearchResults: any[] = [];
  categorySearchLoading = false;
  categorySearchTimeout: any;
  categorySearchSubject = new Subject<string>();
  currentCategoryName: string = '';

  // Category attributes properties
  requiredSaleProperties: any[] = [];
  requiredNonSaleProperties: any[] = [];
  loadingAttributes = false;

  // Import Template Properties
  grantedMerchants: any[] = [];
  merchantTemplates: any[] = [];
  selectedImportMerchantId: string;
  selectedImportTemplateId: string;

  constructor(
    private global: Global,
    private modalService: NgbModal,
    private merchantService: MerchantService,
    private toastr: ToastrService,
    private fb: FormBuilder,
    private http: HttpClient
  ) {
    this.configForm = this.createForm();
  }

  ngOnInit(): void {
    // Load granted merchants when component initializes
    this.loadGrantedMerchants();
  }

  createForm(): FormGroup {
    return this.fb.group({
      configName: ['', Validators.required],
      configSelector: [],
      basePrice: [0, [Validators.required, Validators.min(0)]],
      lowPriceConfig: [0, [Validators.min(0)]],
      categoryId: ['', Validators.required],
      description: ['', Validators.required],
      package: ['', Validators.required],
      stock: [0, [Validators.required, Validators.min(0)]],
      subImages: this.fb.array([]), // Array for multiple image URLs
      sizeChart: [''], // Single image URL
      saleProperties: this.fb.array([]),
      nonSaleProperties: this.fb.array([]),
      templateFileLink: [''],
      receivedFileEmail: ['']
    });
  }

  openSetupTiktokFlashSaleModal() {
    this.global.pageLoading = true;
    this.getPushProductConfigs();
  }

  getPushProductConfigs() {
    this.merchantService.getTiktokPushConfigsForMerchant(this.merchant.merchantId)
      .subscribe(data => {
        this.configForm.reset();
        this.global.pageLoading = false;
        this.tiktokConfigs =
          [{ id: TiktokPushProductConfigComponent.EMPTY_GUID, configName: 'Add New', saleProperties: [], nonSaleProperties: [] }, ...data];
        if (data.length == 0) {
          this.onConfigSelect(TiktokPushProductConfigComponent.EMPTY_GUID);
        } else {
          this.onConfigSelect(data.find(i => i.isActive).id);
          this.currentCategoryName = data.find(i => i.isActive).categoryId;
        }

//        this.generateTestData();
        this.modalService.open(this.tiktokPushProductConfigModal, { windowClass: "xlModal" });
      });

  }

  onConfigSelect(configId: any) {
    this.configForm.reset(); // Reset form before setting new values
    this.selectedConfigId = configId;
    this.currentCategoryName = ''; // Reset current category name
    this.requiredSaleProperties = []; // Reset required properties
    this.requiredNonSaleProperties = []; // Reset required properties

    const selectedConfig = this.tiktokConfigs.find(c => c.id === configId);
    if (selectedConfig) {
      // If there's a categoryId, just show it as plain text
      if (selectedConfig.categoryId) {
        this.currentCategoryName = selectedConfig.categoryId;
      }

      this.configForm.patchValue({
        configName: selectedConfig.configName,
        basePrice: selectedConfig.basePrice,
        lowPriceConfig: selectedConfig.lowPriceConfig,
        categoryId: selectedConfig.categoryId,
        description: selectedConfig.description,
        package: selectedConfig.package,
        stock: selectedConfig.stock,
        sizeChart: selectedConfig.sizeChart,
        templateFileLink: selectedConfig.templateFileLink,
        receivedFileEmail: selectedConfig.receivedFileEmail
      });

      // Ensure Sale & Non-Sale Properties are properly initialized before setting values
      this.setSaleProperties(selectedConfig.saleProperties || []);
      this.setNonSaleProperties(selectedConfig.nonSaleProperties || []);

      const subImagesArray = selectedConfig.subImages ? selectedConfig.subImages.split(',') : [];
      const subImagesFormArray = this.configForm.get('subImages') as FormArray;

      // Clear existing form array
      subImagesFormArray.clear();

      // Add new sub-images to form array
      subImagesArray.forEach(img => {
        subImagesFormArray.push(new FormControl(img.trim()));
      });

      // Load required properties if category is set (for TikTok merchants only)
      if (selectedConfig.categoryId) {
        this.loadCategoryAttributes(selectedConfig.categoryId);
      }
    }
  }

  onCategorySearch(event: any) {
    const searchTerm = event.term;
    if (!searchTerm) {
      this.categorySearchResults = [];
      return;
    }

    // Clear previous timeout
    if (this.categorySearchTimeout) {
      clearTimeout(this.categorySearchTimeout);
    }

    // Set loading state
    this.categorySearchLoading = true;

    // Get market based on merchant type
    const market = this.getMarket();

    // Debounce the search
    this.categorySearchTimeout = setTimeout(() => {
      this.merchantService.searchTiktokCat(market, searchTerm)
        .subscribe(
          (results) => {
            this.categorySearchResults = results;
            this.categorySearchLoading = false;
          },
          (error) => {
            console.error('Error searching categories:', error);
            this.categorySearchLoading = false;
            this.toastr.error('Failed to search categories');
          }
        );
    }, 300); // 300ms debounce
  }

  onCategorySelect(event: any) {
    if (event) {
      this.currentCategoryName = event;
      this.configForm.patchValue({
        categoryId: event
      });
      // Load required properties for TikTok merchants
      this.loadCategoryAttributes(event);
    }
  }

  getMarket(): string {
    return this.merchant.merchantType === 2 ? 'US' : 'GB';
  }

  get saleProperties() {
    return this.configForm.get('saleProperties') as FormArray;
  }

  get nonSaleProperties() {
    return this.configForm.get('nonSaleProperties') as FormArray;
  }

  setNonSaleProperties(properties: any[]) {
    const nonSalePropertiesFormArray = this.configForm.get('nonSaleProperties') as FormArray;
    nonSalePropertiesFormArray.clear(); // Clear existing data

    properties.forEach(prop => {
      nonSalePropertiesFormArray.push(this.fb.group({
        id: [prop.id || this.generateGuid()], // Ensure ID exists
        propertyName: [prop.propertyName, Validators.required],
        value: [prop.value, Validators.required]
      }));
    });
  }


  setSaleProperties(properties: any[]) {
    const salePropertiesFormArray = this.configForm.get('saleProperties') as FormArray;
    salePropertiesFormArray.clear(); // Clear existing data

    properties.forEach(prop => {
      salePropertiesFormArray.push(this.fb.group({
        id: [prop.id || this.generateGuid()], // Ensure ID exists
        propertyName: [prop.propertyName, Validators.required],
        value: [prop.value, Validators.required]
      }));
    });
  }
  addSaleProperty() {
    this.saleProperties.push(this.fb.group({
      propertyName: ['', Validators.required]
    }));
  }

  removeSaleProperty(index: number) {
    this.saleProperties.removeAt(index);
  }

  addNonSaleProperty() {
    this.nonSaleProperties.push(this.fb.group({
      propertyName: ['', Validators.required],
      value: ['', Validators.required]
    }));
  }

  removeNonSaleProperty(index: number) {
    this.nonSaleProperties.removeAt(index);
  }

  onSubmit() {
    if (this.configForm.valid) {
      var formData = {
        ...this.configForm.value,
        id: this.selectedConfigId,
        merchantId: this.merchant.merchantId, // Get merchantId from input merchant
        subImages: this.subImages.value.join(','), // Convert array to comma-separated string
        sizeChart: this.sizeChart.value // Single URL string
      };

      this.global.pageLoading = true;
      this.merchantService.savePushTiktokProductConfig(formData).subscribe(
        (response) => {
          this.global.pageLoading = false;
          this.toastr.success("Configuration saved successfully.");
          this.getPushProductConfigs();
          this.modalService.dismissAll();
        },
        (error) => {
          this.global.pageLoading = false;
          this.toastr.error("Failed to save configuration.");
          this.modalService.dismissAll();
          console.error("Save Error:", error);
        }
      );
    } else {
      this.toastr.error("Please fill in all required fields.");
    }
  }

  get subImages() {
    return this.configForm.get('subImages') as FormArray;
  }

  addSubImage(imageUrl: string = '') {
    this.subImages.push(this.fb.control(imageUrl, Validators.required));
  }

  removeSubImage(index: number) {
    this.subImages.removeAt(index);
  }

  get sizeChart() {
    return this.configForm.get('sizeChart');
  }

  generateTestData() {
    this.configForm.patchValue({
      merchantId: this.merchant?.id || 'test-merchant-id',
      configName: 'Test Config',
      basePrice: 99.99,
      lowPriceConfig: 79.99,
      categoryId: 'test-category',
      description: 'This is a test product configuration.',
      package: 'Test Package',
      stock: 100,
      sizeChart: 'https://via.placeholder.com/150',
    });

    // Sample Sale Properties
    this.saleProperties.clear();
    this.saleProperties.push(this.fb.group({ propertyName: 'Color', value: 'Red' }));
    this.saleProperties.push(this.fb.group({ propertyName: 'Size', value: 'M' }));

    // Sample Non-Sale Properties
    this.nonSaleProperties.clear();
    this.nonSaleProperties.push(this.fb.group({ propertyName: 'Material', value: 'Cotton' }));
    this.nonSaleProperties.push(this.fb.group({ propertyName: 'Brand', value: 'TestBrand' }));

    // Sample SubImages (Comma-Separated URLs)
    this.subImages.clear();
    const testImages = [
      'https://via.placeholder.com/100',
      'https://via.placeholder.com/120',
      'https://via.placeholder.com/140'
    ];
    testImages.forEach(url => {
      this.subImages.push(this.fb.control(url));
    });

    this.toastr.info('Test data loaded.');
  }

  generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0,
        v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  loadGrantedMerchants() {
    this.merchantService.getGrantedMerchants('')
      .subscribe(
        (data) => {
          this.grantedMerchants = data;
        },
        (error) => {
          console.error('Error loading granted merchants:', error);
          this.toastr.error('Failed to load merchants');
        }
      );
  }

  openImportTemplateModal() {
    this.selectedImportMerchantId = null;
    this.selectedImportTemplateId = null;
    this.merchantTemplates = [];
    this.importTemplateModal = this.modalService.open(this.importTemplateModal);
  }

  onImportMerchantSelect(event: any) {
    if (!event) {
      this.merchantTemplates = [];
      return;
    }

    this.merchantService.getTiktokPushConfigsForMerchant(event.merchantId)
      .subscribe(
        (data) => {
          this.merchantTemplates = data;
        },
        (error) => {
          console.error('Error loading templates:', error);
          this.toastr.error('Failed to load templates');
        }
      );
  }

  importTemplate() {
    if (!this.selectedImportTemplateId) {
      return;
    }

    const selectedTemplate = this.merchantTemplates.find(t => t.id === this.selectedImportTemplateId);
    if (!selectedTemplate) {
      this.toastr.error('Selected template not found');
      return;
    }

    // Create a copy of the template
    const templateCopy = {
      ...selectedTemplate,
      id: TiktokPushProductConfigComponent.EMPTY_GUID, // Reset ID for new config
      merchantId: this.merchant.merchantId,
      configName: `${selectedTemplate.configName} (Imported)`,
      isActive: false
    };

    // Update the form with the template values
    this.configForm.patchValue({
      configName: templateCopy.configName,
      basePrice: templateCopy.basePrice,
      lowPriceConfig: templateCopy.lowPriceConfig,
      categoryId: templateCopy.categoryId,
      description: templateCopy.description,
      package: templateCopy.package,
      stock: templateCopy.stock,
      sizeChart: templateCopy.sizeChart,
    });

    // Set the current category name
    this.currentCategoryName = templateCopy.categoryId;

    // Set sale and non-sale properties
    this.setSaleProperties(templateCopy.saleProperties || []);
    this.setNonSaleProperties(templateCopy.nonSaleProperties || []);

    // Set sub-images
    const subImagesArray = templateCopy.subImages ? templateCopy.subImages.split(',') : [];
    const subImagesFormArray = this.configForm.get('subImages') as FormArray;
    subImagesFormArray.clear();
    subImagesArray.forEach(img => {
      subImagesFormArray.push(new FormControl(img.trim()));
    });

    // Close the import modal
    this.importTemplateModal.close();
    this.toastr.success('Template imported successfully');
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    this.global.pageLoading = true;
    this.http.post('/api/Files/Uploads/ProductTemplate', formData)
      .subscribe(
        (response: any) => {
          this.global.pageLoading = false;
          if (response) {
            this.configForm.patchValue({
              templateFileLink: response
            });
            this.toastr.success('Template file uploaded successfully');
          }
        },
        error => {
          this.global.pageLoading = false;
          this.toastr.error('Failed to upload template file');
          console.error('Upload error:', error);
        }
      );
  }

  isTikTokMerchant(): boolean {
    // TikTok merchant types: 2 (TikTok), 6 (TikTokUk), 7 (TikTokDe)
    return this.merchant && (this.merchant.merchantType === 2 || this.merchant.merchantType === 6 || this.merchant.merchantType === 7);
  }

  getRequiredSalePropertiesMessage(): string {
    if (!this.isTikTokMerchant() || this.requiredSaleProperties.length === 0) {
      return '';
    }
    const names = this.requiredSaleProperties.map(p => p.name).join(', ');
    return `(Required: ${names})`;
  }

  getRequiredNonSalePropertiesMessage(): string {
    if (!this.isTikTokMerchant() || this.requiredNonSaleProperties.length === 0) {
      return '';
    }
    const messages = this.requiredNonSaleProperties.map(p => {
      if (p.values && p.values.length > 0) {
        return `${p.name} (${p.values.join(', ')})`;
      }
      return p.name;
    });
    return `(Required: ${messages.join(', ')})`;
  }

  loadCategoryAttributes(categoryId: string) {
    // Only load for TikTok merchants
    if (!this.isTikTokMerchant() || !categoryId) {
      this.requiredSaleProperties = [];
      this.requiredNonSaleProperties = [];
      return;
    }

    this.loadingAttributes = true;
    this.merchantService.getTiktokCategoryAttributes(this.merchant.merchantId, categoryId)
      .subscribe(
        (response: any) => {
          this.loadingAttributes = false;

          if (response && response.data && response.data.attributes && Array.isArray(response.data.attributes)) {
            const attributes = response.data.attributes;

            // Filter required sale properties (Type === "SALES_PROPERTY" and is_requried === true)
            this.requiredSaleProperties = attributes
              .filter((attr: any) => attr.type === 'SALES_PROPERTY')
              .map((attr: any) => ({
                name: attr.name,
                id: attr.id
              }));

            // Filter required non-sale properties (Type !== "SALES_PROPERTY" and is_requried === true)
            this.requiredNonSaleProperties = attributes
              .filter((attr: any) => attr.type !== 'SALES_PROPERTY' && attr.is_requried === true)
              .map((attr: any) => ({
                name: attr.name,
                id: attr.id,
                values: attr.values && Array.isArray(attr.values)
                  ? attr.values.map((val: any) => val.name || val)
                  : []
              }));
          } else {
            this.requiredSaleProperties = [];
            this.requiredNonSaleProperties = [];
          }
        },
        (error) => {
          this.loadingAttributes = false;
          console.error('Error loading category attributes:', error);
          this.toastr.error('Failed to load required properties for this category');
          this.requiredSaleProperties = [];
          this.requiredNonSaleProperties = [];
        }
      );
  }

}
